import { defineStore } from 'pinia'
import { usdtToken } from '@shared/data/token'
import { SharedMarketType } from '@shared/types'
import { toUiDerivativeMarket } from '@shared/transformer/market'
import {
  derivativeCacheApi,
  getIndexerOracleApi,
  getIndexerDerivativesApi,
  cacheRwaPriceFeedService
} from '@shared/service'
import { combineOrderbookRecords } from '@/app/utils/market'
// import { fetchDerivativeStats } from '@/app/services/derivative'
import { TRADE_MAX_SUBACCOUNT_ARRAY_SIZE } from '@/app/utils/constants'
import type {
  OrderSide,
  TradeExecutionSide,
  TradeExecutionType
} from '@injectivelabs/sdk-ts/types'
import type {
  SharedUiDerivativeTrade,
  SharedUiDerivativeMarket,
  SharedUiOrderbookWithSequence
} from '@shared/types'
import type {
  PositionV2,
  ExpiryFuturesMarket,
  DerivativeLimitOrder,
  DerivativeOrderHistory
} from '@injectivelabs/sdk-ts'
import type {
  UiDerivativeMarket,
  UiMarketAndSummary,
  MarketMarkPriceMap,
  ActivityFetchOptions
} from '@/types'

type DerivativeStoreState = {
  marketIdsFromQuery: string[]
  subaccountTradesCount: number
  subaccountOrdersCount: number
  // tickerOpenInterestMap: Record<string, number>
  trades: SharedUiDerivativeTrade[]
  subaccountOrdersOffsetCount: number
  subaccountTradesOffsetCount: number
  subaccountOrderHistoryCount: number
  marketMarkPriceMap: MarketMarkPriceMap
  subaccountOrders: DerivativeLimitOrder[]
  subaccountConditionalOrdersCount: number
  subaccountOrderHistoryOffsetCount: number
  orderbook?: SharedUiOrderbookWithSequence
  subaccountTrades: SharedUiDerivativeTrade[]
  recentlyExpiredMarkets: UiDerivativeMarket[]
  subaccountConditionalOrdersOffsetCount: number
  subaccountOrderHistory: DerivativeOrderHistory[]
  subaccountConditionalOrders: DerivativeLimitOrder[]
}

const initialStateFactory = (): DerivativeStoreState => ({
  recentlyExpiredMarkets: [],
  marketIdsFromQuery: [],
  marketMarkPriceMap: {},
  // tickerOpenInterestMap: {},
  orderbook: undefined,
  trades: [],
  subaccountTrades: [],
  subaccountTradesCount: 0,
  subaccountTradesOffsetCount: 0,
  subaccountOrders: [] as DerivativeLimitOrder[],
  subaccountOrdersCount: 0,
  subaccountOrdersOffsetCount: 0,
  subaccountOrderHistory: [] as DerivativeOrderHistory[],
  subaccountOrderHistoryCount: 0,
  subaccountOrderHistoryOffsetCount: 0,
  subaccountConditionalOrders: [] as DerivativeLimitOrder[],
  subaccountConditionalOrdersCount: 0,
  subaccountConditionalOrdersOffsetCount: 0
})

export const useDerivativeStore = defineStore('derivative', {
  state: (): DerivativeStoreState => initialStateFactory(),
  getters: {
    buys: (state) => state.orderbook?.buys || [],
    sells: (state) => state.orderbook?.sells || [],

    marketByIdOrSlug:
      () =>
      (marketIdOrSlug: string): undefined | SharedUiDerivativeMarket => {
        const sharedDerivativeStore = useSharedDerivativeStore()

        const market = sharedDerivativeStore.marketsWithToken.find(
          (market) =>
            market.marketId === marketIdOrSlug || market.slug === marketIdOrSlug
        )

        return market as SharedUiDerivativeMarket
      },

    perpetualMarkets: () => {
      const sharedDerivativeStore = useSharedDerivativeStore()

      sharedDerivativeStore.marketsWithToken.filter(
        (market) => market && market.subType === SharedMarketType.Perpetual
      )
    },

    expiryFuturesMarkets: () => {
      const sharedDerivativeStore = useSharedDerivativeStore()

      return sharedDerivativeStore.marketsWithToken.filter(
        (market) => market.subType === SharedMarketType.Futures
      )
    },

    activeMarketIds: (state) => {
      const jsonStore = useSharedJsonStore()
      const sharedDerivativeStore = useSharedDerivativeStore()

      return sharedDerivativeStore.marketsWithToken
        .filter(
          (market) =>
            [
              ...jsonStore.expiryMarketIds,
              ...jsonStore.verifiedDerivativeMarketIds
            ].includes(market.marketId) ||
            state.marketIdsFromQuery.includes(market.marketId)
        )
        .map((m) => m.marketId)
    },

    tradableDenoms: () => {
      const sharedDerivativeStore = useSharedDerivativeStore()

      return [
        ...sharedDerivativeStore.marketsWithToken.reduce((denoms, market) => {
          if (!market.isVerified) {
            return denoms
          }

          denoms.add(market.quoteDenom)

          return denoms
        }, new Set() as Set<string>)
      ]
    },

    unverifiedDenoms: () => {
      const sharedDerivativeStore = useSharedDerivativeStore()

      return [
        ...sharedDerivativeStore.marketsWithToken.reduce((denoms, market) => {
          if (market.isVerified) {
            return denoms
          }

          denoms.add(market.quoteDenom)

          return denoms
        }, new Set() as Set<string>)
      ]
    },

    marketsWithSummary: () => {
      const sharedDerivativeStore = useSharedDerivativeStore()

      return sharedDerivativeStore.marketsWithToken
        .map((market) => ({
          market,
          summary: sharedDerivativeStore.marketsSummary.find(
            (summary) => summary.marketId === market.marketId
          )
        }))
        .filter(({ summary }) => summary) as UiMarketAndSummary[]
    }
  },
  actions: {
    // Message actions
    submitDerivativeChase: lazyPiniaAction(
      () => import('./message'),
      'submitDerivativeChase'
    ),
    submitTpSlOrder: lazyPiniaAction(
      () => import('./message'),
      'submitTpSlOrder'
    ),
    submitDerivativeLimitOrder: lazyPiniaAction(
      () => import('./message'),
      'submitDerivativeLimitOrder'
    ),
    submitDerivativeMarketOrder: lazyPiniaAction(
      () => import('./message'),
      'submitDerivativeMarketOrder'
    ),
    submitDerivativeStopLimitOrder: lazyPiniaAction(
      () => import('./message'),
      'submitDerivativeStopLimitOrder'
    ),
    cancelDerivativeOrder: lazyPiniaAction(
      () => import('./message'),
      'cancelDerivativeOrder'
    ),
    submitDerivativeStopMarketOrder: lazyPiniaAction(
      () => import('./message'),
      'submitDerivativeStopMarketOrder'
    ),
    submitExtraTpSlOrders: lazyPiniaAction(
      () => import('./message'),
      'submitExtraTpSlOrders'
    ),
    batchCancelDerivativeOrder: lazyPiniaAction(
      () => import('./message'),
      'batchCancelDerivativeOrder'
    ),

    // Stream actions
    streamDerivativeTrades: lazyPiniaAction(
      () => import('./stream'),
      'streamDerivativeTrades'
    ),
    cancelDerivativeTradesStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelDerivativeTradesStream'
    ),
    streamSubaccountDerivativeTradesHistory: lazyPiniaAction(
      () => import('./stream'),
      'streamSubaccountDerivativeTradesHistory'
    ),
    streamSubaccountDerivativeOrders: lazyPiniaAction(
      () => import('./stream'),
      'streamSubaccountDerivativeOrders'
    ),
    streamMarketsMarkPrices: lazyPiniaAction(
      () => import('./stream'),
      'streamMarketsMarkPrices'
    ),
    cancelMarketsMarkPrices: lazyPiniaAction(
      () => import('./stream'),
      'cancelMarketsMarkPrices'
    ),
    cancelSubaccountDerivativeOrdersStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelSubaccountDerivativeOrdersStream'
    ),
    streamSubaccountDerivativeOrderHistory: lazyPiniaAction(
      () => import('./stream'),
      'streamSubaccountDerivativeOrderHistory'
    ),
    cancelSubaccountDerivativeTradesStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelSubaccountDerivativeTradesStream'
    ),
    cancelSubaccountDerivativeOrderHistoryStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelSubaccountDerivativeOrderHistoryStream'
    ),

    async appendMarketId(marketIdFromQuery: string) {
      const derivativeStore = useDerivativeStore()

      derivativeStore.marketIdsFromQuery = [
        ...derivativeStore.marketIdsFromQuery,
        marketIdFromQuery
      ]
    },

    async fetchRecentlyExpiredMarkets() {
      const sharedTokenStore = useSharedTokenStore()
      const derivativeStore = useDerivativeStore()

      const recentlyExpiredMarkets =
        (await derivativeCacheApi.fetchMarkets()) as Array<ExpiryFuturesMarket>

      const uiMarkets = recentlyExpiredMarkets
        .map((market) => {
          const slug = market.ticker
            .replaceAll('/', '-')
            .replaceAll(' ', '-')
            .toLowerCase()
          const [baseTokenSymbol] = slug.split('-')
          const baseToken = sharedTokenStore.tokenByDenomOrSymbol(
            baseTokenSymbol || ''
          )
          const quoteToken = sharedTokenStore.tokenByDenomOrSymbol(
            market.quoteDenom
          )

          if (!baseToken || !quoteToken) {
            return undefined
          }

          return toUiDerivativeMarket({ market, baseToken, quoteToken, slug })
        })
        .filter((market): market is UiDerivativeMarket => market !== undefined)

      derivativeStore.recentlyExpiredMarkets = uiMarkets
    },

    async getMarketMarkPrice(market: UiDerivativeMarket) {
      const indexerOracleApi = await getIndexerOracleApi()

      const derivativeStore = useDerivativeStore()

      const oraclePrice = await indexerOracleApi.fetchOraclePrice({
        oracleType: market.oracleType,
        baseSymbol: (market as UiDerivativeMarket).oracleBase,
        quoteSymbol: (market as UiDerivativeMarket).oracleQuote
      })

      derivativeStore.marketMarkPriceMap = {
        ...derivativeStore.marketMarkPriceMap,
        [market.marketId]: {
          marketId: market.marketId,
          price: oraclePrice.price
        }
      }
    },

    // async fetchOpenInterest() {
    //   const derivativeStore = useDerivativeStore()

    //   const stats = await fetchDerivativeStats()

    //   const tickerOpenInterestMap = stats.reduce(
    //     (
    //       list,
    //       {
    //         ticker_id: ticker,
    //         open_interest: openInterest
    //       }: { ticker_id: string; open_interest: number }
    //     ) => {
    //       list[ticker] = openInterest

    //       return list
    //     },
    //     {} as Record<string, number>
    //   )

    //   derivativeStore.$patch({
    //     tickerOpenInterestMap
    //   })
    // },

    async fetchOrderbook(marketId: string) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const derivativeStore = useDerivativeStore()

      const currentOrderbookSequence = derivativeStore.orderbook?.sequence || 0
      const latestOrderbook =
        await indexerDerivativesApi.fetchOrderbookV2(marketId)
      const latestOrderbookIsMostRecent =
        latestOrderbook.sequence >= currentOrderbookSequence

      if (latestOrderbookIsMostRecent) {
        derivativeStore.orderbook = latestOrderbook
      }

      // handle race condition between fetch and stream
      derivativeStore.orderbook = {
        sequence: latestOrderbookIsMostRecent
          ? latestOrderbook.sequence
          : currentOrderbookSequence,
        buys: combineOrderbookRecords({
          isBuy: true,
          currentRecords: latestOrderbook.buys,
          updatedRecords: derivativeStore.orderbook?.buys
        }),
        sells: combineOrderbookRecords({
          isBuy: false,
          currentRecords: latestOrderbook.sells,
          updatedRecords: derivativeStore.orderbook?.sells
        })
      }
    },

    async fetchTrades({
      marketId,
      executionSide
    }: {
      marketId: string
      executionSide?: TradeExecutionSide
    }) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const derivativeStore = useDerivativeStore()

      const { trades } = await indexerDerivativesApi.fetchTrades({
        marketIds: [marketId],
        executionSide
      })

      derivativeStore.trades = trades
    },

    async fetchSubaccountOrders(marketIds?: string[]) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const accountStore = useAccountStore()
      const derivativeStore = useDerivativeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      const { orders, pagination } = await indexerDerivativesApi.fetchOrders({
        subaccountId: accountStore.subaccountId,
        isConditional: false,
        marketIds: marketIds || derivativeStore.activeMarketIds,
        pagination: {
          limit: TRADE_MAX_SUBACCOUNT_ARRAY_SIZE
        }
      })

      derivativeStore.$patch({
        subaccountOrders: orders,
        subaccountOrdersCount: pagination.total,
        subaccountOrdersOffsetCount: Math.max(
          0,
          pagination.total - orders.length
        )
      })
    },

    async fetchSubaccountOrderHistory(
      options: undefined | ActivityFetchOptions
    ) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const accountStore = useAccountStore()
      const derivativeStore = useDerivativeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      const filters = options?.filters

      const { orderHistory, pagination } =
        await indexerDerivativesApi.fetchOrderHistory({
          direction: filters?.direction,
          marketIds: filters?.marketIds,
          pagination: options?.pagination,
          isConditional: filters?.isConditional,
          subaccountId: accountStore.subaccountId,
          orderTypes: filters?.orderTypes as unknown as OrderSide[],
          executionTypes: filters?.executionTypes as TradeExecutionType[]
        })

      derivativeStore.$patch({
        subaccountOrderHistory: orderHistory,
        subaccountOrderHistoryCount: pagination.total,
        subaccountOrderHistoryOffsetCount: Math.max(
          0,
          pagination.total - orderHistory.length
        )
      })
    },

    async fetchSubaccountConditionalOrders(marketIds?: string[]) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const accountStore = useAccountStore()
      const derivativeStore = useDerivativeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      const { orders, pagination } = await indexerDerivativesApi.fetchOrders({
        subaccountId: accountStore.subaccountId,
        isConditional: true,
        marketIds: marketIds || derivativeStore.activeMarketIds,
        pagination: {
          limit: TRADE_MAX_SUBACCOUNT_ARRAY_SIZE
        }
      })

      derivativeStore.$patch({
        subaccountConditionalOrders: orders,
        subaccountConditionalOrdersCount: pagination.total,
        subaccountConditionalOrdersOffsetCount: Math.max(
          0,
          pagination.total - orders.length
        )
      })
    },

    async fetchSubaccountTradesHistory(
      options?: undefined | ActivityFetchOptions
    ) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const accountStore = useAccountStore()
      const derivativeStore = useDerivativeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected || !accountStore.subaccountId) {
        return
      }

      const filters = options?.filters

      const { trades, pagination } = await indexerDerivativesApi.fetchTrades({
        direction: filters?.direction,
        subaccountId: accountStore.subaccountId,
        pagination: options?.pagination,
        executionTypes: filters?.executionTypes as TradeExecutionType[],
        marketIds: filters?.marketIds || derivativeStore.activeMarketIds
      })

      derivativeStore.$patch({
        subaccountTrades: trades,
        subaccountTradesCount: pagination.total,
        subaccountTradesOffsetCount: Math.max(
          0,
          pagination.total - trades.length
        )
      })
    },

    async fetchBotsSubaccountOrders({
      marketIds,
      subaccountId
    }: {
      marketIds: string[]
      subaccountId: string
    }) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const derivativeStore = useDerivativeStore()

      const { orders, pagination } = await indexerDerivativesApi.fetchOrders({
        subaccountId,
        isConditional: false,
        marketIds: marketIds || derivativeStore.activeMarketIds,
        pagination: {
          limit: TRADE_MAX_SUBACCOUNT_ARRAY_SIZE
        }
      })

      derivativeStore.$patch({
        subaccountOrders: orders,
        subaccountOrdersCount: pagination.total,
        subaccountOrdersOffsetCount: Math.max(
          0,
          pagination.total - orders.length
        )
      })
    },

    async fetchBotSubaccountTrades({
      subaccountId,
      options
    }: {
      subaccountId: string
      options?: ActivityFetchOptions
    }) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const derivativeStore = useDerivativeStore()

      const filters = options?.filters

      const { trades, pagination } = await indexerDerivativesApi.fetchTrades({
        subaccountId,
        direction: filters?.direction,
        pagination: options?.pagination,
        executionTypes: filters?.executionTypes as TradeExecutionType[],
        marketIds: filters?.marketIds || derivativeStore.activeMarketIds
      })

      derivativeStore.$patch({
        subaccountTrades: trades,
        subaccountTradesCount: pagination.total,
        subaccountTradesOffsetCount: Math.max(
          0,
          pagination.total - trades.length
        )
      })
    },

    async fetchBotSubaccountOrderHistory({
      options,
      subaccountId
    }: {
      subaccountId: string
      options?: ActivityFetchOptions
    }) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const derivativeStore = useDerivativeStore()

      const filters = options?.filters

      const { orderHistory, pagination } =
        await indexerDerivativesApi.fetchOrderHistory({
          subaccountId,
          direction: filters?.direction,
          pagination: options?.pagination,
          isConditional: filters?.isConditional,
          executionTypes: filters?.executionTypes as TradeExecutionType[],
          marketIds: filters?.marketIds || derivativeStore.activeMarketIds,
          orderTypes: filters?.orderTypes as unknown as OrderSide[]
        })

      derivativeStore.$patch({
        subaccountOrderHistory: orderHistory,
        subaccountOrderHistoryCount: pagination.total,
        subaccountOrderHistoryOffsetCount: Math.max(
          0,
          pagination.total - orderHistory.length
        )
      })
    },

    async fetchRWAMarketIsOpen(market: UiDerivativeMarket) {
      const jsonStore = useSharedJsonStore()

      const isSedaRwaMarket = jsonStore.isSedaMarket(market.marketId)
      const isPythRwaMarket = jsonStore.isTradeFiMarket(market.marketId)

      if ((!isPythRwaMarket && !isSedaRwaMarket) || !market.oracleBase) {
        return true
      }

      if (isSedaRwaMarket) {
        return await cacheRwaPriceFeedService.fetchSedaRwaMarketOpenNoThrow(
          market.oracleBase.split('/')?.[0] || market.marketId
        )
      }

      return await cacheRwaPriceFeedService.fetchPythRwaMarketOpenNoThrow(
        market.oracleBase
      )
    },

    async fetchPythRWAMarketIsOpen(oracle: string) {
      return await cacheRwaPriceFeedService.fetchPythRwaMarketOpenNoThrow(
        oracle
      )
    },

    async fetchSedaRwaMarketIsOpen(oracle: string) {
      return await cacheRwaPriceFeedService.fetchSedaRwaMarketOpenNoThrow(
        oracle
      )
    },

    updateMarkPriceMapFromPosition(positions: PositionV2[]) {
      const derivativeStore = useDerivativeStore()

      const markPricesMap = positions.reduce((markPrices, position) => {
        const market = derivativeStore.marketByIdOrSlug(position.marketId)

        return {
          ...markPrices,
          [position.marketId]: {
            marketId: position.marketId,
            price: toHumanReadable(
              position.markPrice,
              market?.quoteToken.decimals || usdtToken.decimals
            ).toFixed()
          }
        }
      }, {} as MarketMarkPriceMap)

      derivativeStore.$patch({
        marketMarkPriceMap: {
          ...derivativeStore.marketMarkPriceMap,
          ...markPricesMap
        }
      })
    },

    async cancelSubaccountStream() {
      const derivativeStore = useDerivativeStore()

      await Promise.all([
        derivativeStore.cancelSubaccountDerivativeOrdersStream(),
        derivativeStore.cancelSubaccountDerivativeTradesStream(),
        derivativeStore.cancelSubaccountDerivativeOrderHistoryStream()
      ])
    },

    resetOrderbookAndTrades() {
      const derivativeStore = useDerivativeStore()

      derivativeStore.$patch({
        trades: [],
        orderbook: undefined
      })
    },

    async resetSubaccount() {
      const derivativeStore = useDerivativeStore()
      const initialState = initialStateFactory()

      await derivativeStore.cancelSubaccountStream()

      derivativeStore.$patch({
        subaccountOrders: initialState.subaccountOrders,
        subaccountTrades: initialState.subaccountTrades,
        subaccountTradesCount: initialState.subaccountOrdersCount,
        subaccountOrdersCount: initialState.subaccountOrdersCount,
        subaccountOrderHistory: initialState.subaccountOrderHistory,
        subaccountOrderHistoryCount: initialState.subaccountOrderHistoryCount
      })
    },

    reset() {
      const derivativeStore = useDerivativeStore()

      const { trades, orderbook, subaccountTrades, subaccountOrders } =
        initialStateFactory()

      derivativeStore.$patch({
        trades,
        orderbook,
        subaccountTrades,
        subaccountOrders
      })
    }
  }
})
